'use client';

import axios from 'axios';
import React, { useEffect } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { SectionDesign } from '@diritto/common/home/constants/section-constants';
import { SectionDto } from '@diritto/common/home/dtos/section.dtos';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { SectionFancyShareList } from 'app-components/sections/FancyShareList';
import { SectionReplyList } from 'app-components/sections/ReplyList';
import { SectionContentList } from 'app-components/sections/SectionContentList';
import { useGetSectionQuery } from 'queries/section/useGetSectionsQuery';
import { TransitionFullPageLoading } from 'ui-components/common/Loading';
import { ErrorStatusView } from 'ui-components/error/ErrorStatusView';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { SentryService } from 'utils/SentryService';

type ContentPageProps = {
  sectionSlug: string;
  isApp?: boolean;
};

export const Section = ({ section }: { section: SectionDto }) => {
  switch (section.design) {
    case SectionDesign.Reply:
      return <SectionReplyList section={section} />;
    case SectionDesign.FancyShare:
      return <SectionFancyShareList section={section} />;
    case SectionDesign.Content:
    case SectionDesign.ContentLite:
    case SectionDesign.ContentCompact:
    case SectionDesign.ExclusiveContent:
      return <SectionContentList section={section} />;
    default:
      SentryService.captureMessage(`Section design not found: ${JSON.stringify(section)}`);
      return null;
  }
};

export const SectionPage = ({ sectionSlug, isApp }: ContentPageProps) => {
  useInAppHeader({ screen: WebViewScreen.Section });
  useInAppStatusBar(WebViewScreen.Section);

  const { data, error, isLoading } = useGetSectionQuery(sectionSlug);

  useEffect(() => {
    if (data?.section?.title) {
      DirittoAppBridge.showAppHeader({ screen: WebViewScreen.Section, title: data?.section?.title });
    }
  }, [data?.section?.title]);

  const statusCode = (axios.isAxiosError(error) && error.response?.status) || undefined;
  if (statusCode) {
    return (
      <MutableHeaderLayout isApp={isApp}>
        <ErrorStatusView statusCode={statusCode} />
      </MutableHeaderLayout>
    );
  }

  return (
    <MutableHeaderLayout title={data?.section.title ?? ''} isApp={isApp} showTopUp>
      <TransitionFullPageLoading isShow={!data || isLoading} />
      {data && <Section section={data.section} />}
    </MutableHeaderLayout>
  );
};
